"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Testimonial } from "@/lib/testimonials";
import { cn } from "@/lib/utils";
import { CompanyMark, EASE, Headshot, Accent, ReviewIndex, SectionIntro, Stars, renderQuote } from "./shared";

/**
 * Variant: "Spotlight". Proof-led.
 * One review at a time. The person's portrait fills the left half, the number sits on it,
 * the hook and story fill the right. A row of faces and a previous / next pair switch
 * between clients; it also advances on its own until the pointer or focus is inside.
 */

/** How long each review stays up before the next one */
const INTERVAL_MS = 7000;

/** False on the server and during hydration, true once mounted. Nothing to subscribe to. */
const noop = () => () => {};
const useMounted = () => useSyncExternalStore(noop, () => true, () => false);

export const Spotlight = ({ testimonials: TESTIMONIALS }: { testimonials: Testimonial[] }) => {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  // The timer line only starts after mount: `reduce` is unknown on the server, so deciding
  // it there would render a different element from the client and fail hydration.
  const mounted = useMounted();

  const count = TESTIMONIALS.length;
  const t = TESTIMONIALS[active];
  const go = (i: number) => setActive(((i % count) + count) % count);

  // Auto-advance. Pauses while the pointer or focus is inside; off under reduced motion.
  useEffect(() => {
    if (reduce || paused || count < 2) return;
    const id = window.setTimeout(() => setActive((i) => (i + 1) % count), INTERVAL_MS);
    return () => window.clearTimeout(id);
  }, [active, paused, reduce, count]);

  if (count === 0) return null;

  const running = mounted && !reduce && !paused && count > 1;

  return (
    <section id="reviews" aria-labelledby="reviews-heading" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-[95vw] px-4 sm:max-w-[90vw] sm:px-6 md:max-w-[1200px] md:px-8">
        <SectionIntro
          headingId="reviews-heading"
          title={
            <>
              What <Accent>clients say.</Accent>
            </>
          }
        />

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: reduce ? 0 : 0.9, ease: EASE }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          className="rounded-[2rem] border border-black/10 p-2"
        >
          <div className="relative grid overflow-hidden rounded-[calc(2rem-0.5rem)] bg-brand-paper md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            {/* The person, in colour */}
            <div className="relative aspect-[4/3] sm:aspect-[16/10] md:aspect-auto md:min-h-[580px]">
              <AnimatePresence initial={false}>
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: reduce ? 0 : 0.7, ease: EASE }}
                  className="absolute inset-0"
                >
                  <Headshot t={t} fill sizes="(min-width: 768px) 42vw, 95vw" />
                  {/* Fades the portrait into the panel so the seam never shows */}
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-t from-brand-paper via-brand-paper/20 to-transparent md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-brand-paper"
                  />
                </motion.div>
              </AnimatePresence>

              {/* The number, pinned to the portrait */}
              <div className="absolute bottom-5 left-5 sm:bottom-7 sm:left-7">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.p
                    key={t.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: reduce ? 0 : 0.45, ease: EASE }}
                    className="-rotate-2 rounded-lg bg-brand-mint px-5 py-4 text-brand-ink shadow-[0_20px_50px_-20px_rgba(43,196,138,0.6)]"
                  >
                    <span className="block font-heading text-4xl uppercase leading-none tracking-tight sm:text-5xl">
                      {t.result.value}
                    </span>
                    <span className="mt-1.5 block max-w-[18ch] text-xs font-medium leading-snug text-brand-ink/75">
                      {t.result.label}
                    </span>
                  </motion.p>
                </AnimatePresence>
              </div>
            </div>

            {/* The words */}
            <div className="relative flex flex-col p-7 sm:p-9 md:p-12">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: reduce ? 0 : 0.45, ease: EASE }}
                  className="flex flex-1 flex-col"
                >
                  <Stars rating={t.rating} />
                  <h3 className="mt-6 font-heading text-3xl uppercase leading-[0.95] text-brand-ink sm:text-4xl lg:text-5xl">
                    {t.hook}
                  </h3>
                  <blockquote className="mt-6 text-lg leading-snug text-brand-ink/70 sm:text-xl md:text-[1.35rem]">
                    {renderQuote(t.quote)}
                  </blockquote>

                  <div className="mt-auto flex flex-wrap items-end justify-between gap-x-8 gap-y-4 pt-10">
                    <div>
                      <span className="block text-brand-ink">{t.name}</span>
                      <span className="mt-0.5 block text-sm text-brand-ink/50">{t.role}</span>
                    </div>
                    <CompanyMark t={t} />
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Every client as a face, a timer line, and previous / next */}
              <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-black/10 pt-6">
                <div role="tablist" aria-label="Clients" className="flex items-center gap-3">
                  {TESTIMONIALS.map((item, i) => {
                    const isActive = i === active;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        aria-label={`${item.name}, ${item.client}`}
                        tabIndex={isActive ? 0 : -1}
                        onClick={() => setActive(i)}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                            e.preventDefault();
                            go(i + 1);
                          } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                            e.preventDefault();
                            go(i - 1);
                          }
                        }}
                        className={cn(
                          "rounded-xl transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 focus-visible:ring-offset-brand-paper",
                          isActive ? "ring-2 ring-brand-accent" : "opacity-45 hover:opacity-90",
                        )}
                      >
                        <Headshot t={item} size={44} />
                      </button>
                    );
                  })}
                </div>

                <span aria-hidden className="h-px min-w-8 flex-1 overflow-hidden bg-black/10">
                  {running ? (
                    <motion.span
                      key={`timer-${active}`}
                      className="block h-full w-full origin-left bg-brand-accent"
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: INTERVAL_MS / 1000, ease: "linear" }}
                    />
                  ) : (
                    <span className="block h-full w-full bg-brand-accent/40" />
                  )}
                </span>

                {count > 1 && (
                  <div className="flex shrink-0 items-center gap-2">
                    <Button type="button" variant="outline" size="icon" aria-label="Previous review" onClick={() => go(active - 1)}>
                      <ArrowLeft className="size-4" />
                    </Button>
                    <Button type="button" variant="outline" size="icon" aria-label="Next review" onClick={() => go(active + 1)}>
                      <ArrowRight className="size-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        <ReviewIndex testimonials={TESTIMONIALS} exclude={[t.id]} />
      </div>
    </section>
  );
};
